/** Display string used when the source reports a Dynamic fee tier. */
export const DYNAMIC_FEE_DISPLAY = "0.30%";

/** Canonical Uniswap standard fee tiers for filters / "Other" bucketing. */
export const STANDARD_FEE_TIERS = ["0.01%", "0.05%", "0.30%", "1.00%"] as const;

export type StandardFeeTier = (typeof STANDARD_FEE_TIERS)[number];

const STANDARD_FEE_TIER_SET = new Set<string>(STANDARD_FEE_TIERS);

export function isStandardFeeTier(feeTier: string): boolean {
	return STANDARD_FEE_TIER_SET.has(feeTier);
}

export function isOtherFeeTier(feeTier: string): boolean {
	return !STANDARD_FEE_TIER_SET.has(feeTier);
}

/** Format fee % for display. Keep ≥2 decimals; up to 4 when needed (no round-away of 3rd+). */
export function formatFeeTierPercent(pct: number): string {
	if (!Number.isFinite(pct) || pct < 0) return "0.00%";
	const trimmed = pct.toFixed(4).replace(/0+$/, "").replace(/\.$/, "");
	const frac = trimmed.split(".")[1] ?? "";
	if (frac.length <= 2) return `${pct.toFixed(2)}%`;
	return `${trimmed}%`;
}

/** Normalize a display fee string (e.g. `"0.3%"`, `"Dynamic"`). */
export function normalizeFeeTierFromDisplay(feeTier: string): string {
	if (feeTier === "Dynamic") return DYNAMIC_FEE_DISPLAY;
	const numericValue = parseFloat(feeTier.replace("%", "").trim());
	if (!Number.isFinite(numericValue)) return feeTier;
	return formatFeeTierPercent(numericValue);
}

/** Normalize Uniswap raw fee (hundredths of a bip, e.g. `3000` → `0.30%`). */
export function normalizeFeeTierFromRaw(raw: number | string): string {
	if (raw === "Dynamic") return DYNAMIC_FEE_DISPLAY;
	const n = typeof raw === "string" ? parseInt(raw, 10) : raw;
	if (!Number.isFinite(n)) return DYNAMIC_FEE_DISPLAY;
	return formatFeeTierPercent((n / 1e6) * 100);
}

/**
 * Sample / legacy path: decimal fraction (`0.003` → `0.30%`) or percent string.
 */
export function normalizeFeeTierFromFractionOrDisplay(feeTier: number | string): string {
	if (feeTier === "Dynamic") return DYNAMIC_FEE_DISPLAY;
	if (typeof feeTier === "string") return normalizeFeeTierFromDisplay(feeTier);
	return formatFeeTierPercent(Number(feeTier) * 100);
}
